import { IconDotsVertical } from '@tabler/icons-react-native';
import { ShowErrors } from 'app/components/show-errors';
import { Column } from 'app/design/primitives/box';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { Thumbnail } from 'app/design/primitives/thumbnail';
import { useNavigationOptions } from 'app/hooks/navigation';
import { localeAwareFormat } from 'app/lib/date';
import { useProfile, useTrustedBy } from 'app/network/profile';
import { createTheme as createDSTheme } from 'app/shopify';
import type { NavigationScreen } from 'app/types/navigation';
import type { TrustMember } from 'app/types/profile';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';

const theme = createDSTheme();

function TrustMemberCard({
  member,
  onPress,
  onMenu,
}: {
  member: TrustMember;
  onPress: () => void;
  onMenu?: () => void;
}) {
  const { t } = useTranslation('neighbour-profile');
  const location = [member.city, member.postalCode].filter(Boolean).join(' · ');
  return (
    <Pressable
      onPress={onPress}
      style={{
        minHeight: 110,
        padding: 15,
        borderRadius: 16,
        backgroundColor: '#F7F6F3',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 15,
      }}
    >
      <Thumbnail height={80} source={{ uri: member.picture ?? undefined }} />
      <Column flex={1} gap="spacing.general.x1">
        <Text fontWeight="700" variant="bodyBase">
          {member.firstName} {member.lastName}
        </Text>
        {location || member.distance !== undefined ? (
          <Text variant="bodySmall">
            {location}
            {location && member.distance !== undefined ? ' · ' : ''}
            {member.distance !== undefined
              ? t('trust.distance', { distance: Math.max(1, Math.round(member.distance)) })
              : ''}
          </Text>
        ) : null}
        <Text color="text.secondary" variant="bodySmall">
          {t('trust.since', {
            date: localeAwareFormat(new Date(member.trustedSince), t('dateFormat_MMMYYYY', { ns: 'common' })),
          })}
        </Text>
      </Column>
      {onMenu ? (
        <Pressable
          hitSlop={12}
          onPress={onMenu}
          accessibilityRole="button"
          accessibilityLabel={t('shares.memberActions.more', { name: member.firstName })}
        >
          <IconDotsVertical color={theme.colors['text.primary']} size={24} strokeWidth={ICON_STROKE_WIDTH} />
        </Pressable>
      ) : null}
    </Pressable>
  );
}

export const TrustedByMembers: NavigationScreen<'trusted-members'> = ({ navigation, route }) => {
  const { t } = useTranslation('neighbour-profile');
  const profile = useProfile(route.params.userId);
  const trustedBy = useTrustedBy(route.params.userId);
  const members = trustedBy.data ?? [];
  const common = members.filter((member) => member.isInViewerTrustCircle);
  const others = members.filter((member) => !member.isInViewerTrustCircle);
  const subjectName = [profile.data?.firstName, profile.data?.lastName].filter(Boolean).join(' ');
  useNavigationOptions(navigation, {
    headerTitle: () => (
      <Text fontWeight="700" variant="bodyBase">
        {subjectName}
      </Text>
    ),
  });
  const renderMembers = (items: TrustMember[]) => (
    <Column gap="spacing.general.x4">
      {items.map((member) => (
        <TrustMemberCard
          key={member.userId}
          member={member}
          onPress={() => navigation.navigate('neighbour-profile', { userId: member.userId })}
        />
      ))}
    </Column>
  );
  return (
    <Page paddingStyle={{ paddingTop: 16, paddingBottom: 24, paddingLeft: 16, paddingRight: 16 }}>
      {common.length ? (
        <>
          <Text fontWeight="600" variant="bodyBase" style={{ marginBottom: 16 }}>
            {t('trust.commonHeading', { name: profile.data?.firstName })}
          </Text>
          {renderMembers(common)}
        </>
      ) : null}
      {others.length ? (
        <>
          <Text fontWeight="600" variant="bodyBase" style={{ marginTop: common.length ? 28 : 0, marginBottom: 16 }}>
            {t('trust.otherHeading', { name: profile.data?.firstName })}
          </Text>
          {renderMembers(others)}
        </>
      ) : null}
      {!members.length && !trustedBy.isPending ? (
        <Text color="text.tertiary" textAlign="center" style={{ marginTop: 40 }}>
          {t('trust.empty')}
        </Text>
      ) : null}
      <ShowErrors error={profile.error ?? trustedBy.error} />
    </Page>
  );
};
